import { Button } from "@/components/ui/button";
import { getPopupImage } from "@/lib/popupImages";
import { getAccommodation } from "@/lib/accommodationAmenities";
import { safeColor, useCopyOverride } from "@/lib/copyOverrides";
import { PopupImageCarousel } from "@/components/marketing/PopupImageCarousel";

interface OvernightPopupCardProps {
  onCta: () => void;
}

interface PopupCard {
  id?: string;
  title?: string;
  price?: string;
  bullets?: string[];
  image?: string;
}

function parseCards(raw: string): PopupCard[] {
  if (!raw || !raw.trim()) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((c): c is PopupCard => Boolean(c) && typeof c === "object")
      .filter((c) => typeof c.title === "string" && c.title.trim().length > 0);
  } catch {
    return [];
  }
}

export function OvernightPopupCard({ onCta }: OvernightPopupCardProps) {
  const headline = useCopyOverride("popup.headline");
  const price = useCopyOverride("popup.price");
  const subline = useCopyOverride("popup.subline");
  const cta = useCopyOverride("popup.cta");
  const imageId = useCopyOverride("popup.image");
  const imagesCsv = useCopyOverride("popup.images");
  const accommodationsCsv = useCopyOverride("popup.accommodations");
  const cardsRaw = useCopyOverride("popup.cards");
  const accent = safeColor(useCopyOverride("popup.accent"));
  const buttonFg = safeColor(useCopyOverride("popup.buttonFg"));

  const image = getPopupImage(imageId);
  const remoteImages = imagesCsv
    .split(",")
    .map((s) => s.trim())
    .filter((s) => /^https:\/\//i.test(s));

  const options = accommodationsCsv
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .map((id) => getAccommodation(id))
    .filter((o): o is NonNullable<typeof o> => Boolean(o));

  const customCards = parseCards(cardsRaw);

  return (
    <div className="flex flex-col">
      {remoteImages.length > 0 ? (
        <PopupImageCarousel
          images={remoteImages}
          alt={headline || "Promotion"}
          className="w-full h-44 sm:h-52"
        />
      ) : (
        <img
          src={image.src}
          alt={image.label}
          className="w-full h-44 sm:h-52 object-cover rounded-xl"
          loading="eager"
        />
      )}

      <div className="pt-4 space-y-3 text-center">
        <h2 className="font-heading text-2xl font-bold text-charcoal">{headline}</h2>
        {price && (
          <p className="text-lg font-semibold text-coral-orange" style={{ color: accent }}>
            {price}
          </p>
        )}
        {subline && <p className="text-sm text-charcoal/70">{subline}</p>}

        {customCards.length > 0 ? (
          <div className="space-y-3 pt-1">
            {customCards.map((card, i) => (
              <div
                key={card.id || `${card.title}-${i}`}
                className="text-left rounded-xl border border-border p-3"
              >
                {card.image && /^https:\/\//i.test(card.image) && (
                  <img
                    src={card.image}
                    alt={card.title || "Option"}
                    className="w-full h-28 object-cover rounded-lg mb-3"
                    loading="lazy"
                    referrerPolicy="no-referrer"
                  />
                )}
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-sm font-semibold text-charcoal">{card.title}</span>
                  {card.price && (
                    <span
                      className="text-sm font-semibold text-coral-orange"
                      style={{ color: accent }}
                    >
                      {card.price}
                    </span>
                  )}
                </div>
                {Array.isArray(card.bullets) && card.bullets.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {card.bullets
                      .filter((b) => typeof b === "string" && b.trim())
                      .map((b) => (
                        <span
                          key={b}
                          className="rounded-full bg-muted px-3 py-1 text-xs text-charcoal/80"
                        >
                          {b}
                        </span>
                      ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : options.length > 0 && (
          <div className="space-y-3 pt-1">
            {options.map((option) => {
              const Icon = option.icon;
              return (
                <div key={option.id} className="text-left">
                  <div className="flex items-center gap-2 text-sm font-semibold text-charcoal">
                    <Icon className="w-4 h-4 text-teal" />
                    <span>{option.label}</span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {option.amenities.map((amenity) => (
                      <span
                        key={amenity}
                        className="rounded-full bg-muted px-3 py-1 text-xs text-charcoal/80"
                      >
                        {amenity}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <Button
          className="w-full mt-2"
          size="lg"
          onClick={onCta}
          style={{ backgroundColor: accent, color: buttonFg }}
        >
          {cta}
        </Button>
      </div>
    </div>
  );
}
